/**
 * FilePreviewChip - Individual file preview chip.
 *
 * Shows file type icon, truncated filename, size, and remove button.
 * Includes progress ring during upload.
 */

import { X, File, Image, FileText } from 'lucide-react'
import { ProgressRing } from '../ToolDisplay/ProgressRing'

export interface FileAttachment {
  id: string
  file: File
  preview?: string // Data URL for images
  uploading: boolean
  uploadProgress: number
  error?: string
}

interface FilePreviewChipProps {
  attachment: FileAttachment
  onRemove: (id: string) => void
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image
  if (type === 'application/pdf' || type.startsWith('text/')) return FileText
  return File
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

export function FilePreviewChip({ attachment, onRemove }: FilePreviewChipProps) {
  const Icon = getFileIcon(attachment.file.type)
  const isImage = attachment.file.type.startsWith('image/')

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${
        attachment.error
          ? 'bg-red-500/10 border-red-500/30'
          : attachment.uploading
          ? 'bg-blue-500/10 border-blue-500/30'
          : 'bg-surface border-surface-border'
      }`}
    >
      {/* Preview thumbnail or icon */}
      {isImage && attachment.preview ? (
        <img
          src={attachment.preview}
          alt={attachment.file.name}
          className="w-5 h-5 rounded object-cover"
        />
      ) : (
        <Icon className="w-4 h-4 text-text-muted" />
      )}

      {/* Filename */}
      <span className="text-xs truncate max-w-[100px]" title={attachment.file.name}>
        {attachment.file.name}
      </span>

      {/* Size */}
      <span className="text-[10px] text-text-muted">
        {formatSize(attachment.file.size)}
      </span>

      {/* Upload progress or remove button */}
      {attachment.uploading ? (
        <ProgressRing status="running" size={14} />
      ) : (
        <button
          onClick={() => onRemove(attachment.id)}
          className="p-0.5 hover:bg-white/10 rounded-full transition-colors"
          aria-label={`Remove ${attachment.file.name}`}
        >
          <X className="w-3 h-3 text-text-muted hover:text-text" />
        </button>
      )}

      {/* Error indicator */}
      {attachment.error && (
        <span className="text-[10px] text-red-400">{attachment.error}</span>
      )}
    </div>
  )
}

export default FilePreviewChip
